import { CognitoUser } from 'amazon-cognito-identity-js';
import { Action, Middleware, Reducer } from "redux";
import { ApplicationState } from ".";
import * as Model from "../models/Index";
import { AppStore, Message, UserDetail } from "../models/Index";

//#region Constants
export const IS_SHOW_MAINTENANCE_SCREEN = "IS_SHOW_MAINTENANCE_SCREEN"
//#endregion

interface FetchState {
  requested: boolean;
  loading: boolean;
  success?: boolean;
  data?: any;
  error?: any;
}

interface FormState<T = any> {
  initialValue: T;
  value: T;
  error: boolean;
  pristine: boolean;
  errors: {
    [fieldName in keyof T]?: string;
  };
  touched: {
    [fieldName in keyof T]?: boolean;
  };
  submitting: boolean;
  validationNeeded: boolean;
}

export interface AppState {
  loading: boolean;
  initAppLoading: boolean;
  messages: Message[];
  user: UserDetail | null;
  resetPassUserName: string;
  isPasswordResetRequired: boolean;
  fetch: {
    [url: string]: FetchState;
  };
  forms: {
    [url: string]: FormState;
  };
  container: {
    producerCode: number;
    date: string;
  };
  searchParams: string;
  pageSize: number;
  data: AppStore;
}

const generateRandomUUID = () => {
  // UUIDs have 16 byte values
  const bytes = new Uint8Array(16);

  // Seed bytes with cryptographically random values
  crypto.getRandomValues(bytes);
  // Set required fields for an RFC 4122 random UUID
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  // Convert bytes to hex and format appropriately
  const uuid = Array.prototype.map
    .call(bytes, (b, i) => {
      // Left-pad single-character values with 0,
      // Convert to hexadecimal,
      // Add dashes
      return (
        (b < 16 ? "0" : "") +
        b.toString(16) +
        (i % 2 && i < 10 && i > 2 ? "-" : "")
      );
    })
    .join("");
  // Return the string
  return uuid;
};

//---------------------------
// ログイン
//---------------------------
// コマンド
export interface ChangePassAction {
  type: "CHANGE_PASS";
  payload: {
    currentPassword: string;
    newPassword: string;
  };
}
export interface LoginAction {
  type: "LOGIN";
  payload: {
    credential: Model.Credential;
  };
}
export interface verificationCodeAction {
  type: "VERIFICATION_CODE";
  payload: {
    code: string;
  };
}
export interface cancleVerificationCodeAction {
  type: "CANCLE_VERIFICATION_CODE";
}
export interface InitAppAction {
  type: "INIT_APP";
}
export interface ClearSessionAction {
  type: "CLEAR_SESSION";
}
// イベント
export interface AppLoadedAction {
  type: "APP_LOADED";
}
export interface AppLoadingAction {
  type: "APP_LOADING";
}
export interface SessionFetchedAction {
  type: "SESSION_FETCHED";
  payload: { cognitoUser: CognitoUser };
}
export interface SessionExpiredAction {
  type: "SESSION_EXPIRED";
}
export interface AuthorizeAction {
  type: "AUTHORIZE";
  payload: { userProfile: UserDetail };
}
export interface ResetPassAction {
  type: "RESET_PASS";
  payload: {
    resetPassUserName: string;
  };
}
export interface ExitResetPassAction {
  type: "EXIT_RESET_PASS";
}
//---------------------------
// メッセージ表示
//---------------------------
// コマンド
export interface ShowMessageAction {
  type: "SHOW_MESSAGE";
  payload: {
    message: Message;
    dissmissAfterMs: number;
  };
}
export interface DismissMessageAction {
  type: "DISMISS_MESSAGE";
  payload: { messageId: string };
}
// イベント
export interface MessageAddedAction {
  type: "MESSAGE_ADDED";
  payload: Message;
}
export interface MessageRemovedAction {
  type: "MESSAGE_REMOVED";
  payload: Message;
}

//---------------------------
// データ取得
//---------------------------
// コマンド
type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";
export interface FetchAction {
  type: "FETCH";
  payload: {
    fetchId: string;
    url: string;
    method: HttpMethod;
    params?: { [key: string]: any };
    multipart: boolean;
    json: boolean;
    retry: number;
  };
}
export interface InitFetchAction {
  type: "INIT_FETCH";
  payload: { fetchId: string };
}
export interface DiscardFetchAction {
  type: "DISCARD_FETCH";
  payload: { fetchId: string };
}
export interface DownloadFileAction {
  type: "DOWNLOAD_FILE";
  payload: {
    fetchId: string;
    title: string;
    expectedFilename: string;
    expectedMimeType: string;
    url: string;
    params?: { [key: string]: any };
    method?: string;
    searchParams?: boolean;
  };
}
// イベント
export interface FetchCompletedAction {
  type: "FETCH_COMPLETED";
  payload: { fetchId: string; success: boolean; data?: any; error?: any };
}

export interface SetContainerAction {
  type: "SET_CONTAINER";
  payload: { producerCode: number; date: string };
}

export interface SetSearchParamsAction {
  type: "SET_SEARCH_PARAMS";
  payload: { params: string };
}

export interface SetPageSizeAction {
  type: "SET_PAGE_SIZE";
  payload: { pageSize: number };
}

//---------------------------
// データ編集
//---------------------------
// コマンド
export interface InitFormAction {
  type: "INIT_FORM";
  payload: { formId: string; initialValue: { [key: string]: any } };
}
export interface TouchFormAction {
  type: "TOUCH_FORM";
  payload: { formId: string; key: string };
}
export interface ChangeFormAction {
  type: "CHANGE_FORM";
  payload: { formId: string; name: string; newValue: any };
}
export interface ValidateFormAction {
  type: "VALIDATE_FORM";
  payload: { formId: string; errors: FormState["errors"] };
}
export interface SubmitFormAction {
  type: "SUBMIT_FORM";
  payload: { formId: string };
}
export interface ResetFormAction {
  type: "RESET_FORM";
  payload: { formId: string };
}
export interface DiscardFormAction {
  type: "DISCARD_FORM";
  payload: { formId: string };
}
// イベント
export interface FormSubmittedAction {
  type: "FORM_SUBMITTED";
  payload: { formId: string };
}
export interface FormSubmissionCompletedAction {
  type: "FORM_SUBMISSION_COMPLETED";
  payload: { formId: string; success: boolean; returnData?: any; error?: any };
}
// Control action
export interface UpdateControlPropertiesAction {
  type: "UPDATE_PROPERTIES_OF_CONTROL",
  payload: object,
  id: string
}

export interface UpdateControlHeightAction {
  type: "UPDATE_CONTROL_HEIGHT",
  payload: any,
  id: string,
  x: any,
  y: any,
}

export interface RotateControlAction {
  type: "ROTATE",
  payload:any,
}
export interface RemoveControlAction {
  type: "REMOVE_SHAPES",
  payload: number,
  id: string
}
export interface CutControlAction {
  type: "CUT_SHAPES",
  payload: number,
  id: string
}
export interface UpdateUserCurveAction {
  type: "UPDATE_USER_CURVE",
  payload: {
    controlId: string,
    data: any
  },
}
export interface DeleteUserCurveAction {
  type: "DELETE_USER_CURVE",
  payload: {
    controlId: string,
    data: any
  },
}

export interface UpdateQuickPropertiesAction{
    type: "UPDATE_QUICK_PROPERTIES";
    payload: { field:string, value:boolean};
}

export interface MappedDataAction{
  type: "MAPPED_DATA"
  payload:{
    elements:any;
  }
}

export interface SaveListGraphNameAction{
  type: "SAVE_LIST_GRAPH_NAME",
  payload: {
    data : any
  }
}

export interface DeleteTcGroupAction{
  type: "DELETE_TC_GROUP",
  payload:{
    tc_id:any
  }
}

export interface SaveListGraphAction{
  type: "SAVE_LIST_GRAPH",
  payload:{
    projectData:any,
  }
}
  
type KnownAction =
  | LoginAction
  | verificationCodeAction
  | cancleVerificationCodeAction
  | ChangePassAction
  | InitAppAction
  | AppLoadedAction
  | AppLoadingAction
  | ClearSessionAction
  | SessionFetchedAction
  | SessionExpiredAction
  | ShowMessageAction
  | DismissMessageAction
  | MessageAddedAction
  | MessageRemovedAction
  | FetchAction
  | InitFetchAction
  | DiscardFetchAction
  | DownloadFileAction
  | FetchCompletedAction
  | InitFormAction
  | TouchFormAction
  | ChangeFormAction
  | ValidateFormAction
  | SubmitFormAction
  | ResetFormAction
  | DiscardFormAction
  | FormSubmittedAction
  | FormSubmissionCompletedAction
  | SetContainerAction
  | SetSearchParamsAction
  | SetPageSizeAction
  | UpdateControlPropertiesAction
  | RemoveControlAction
  | CutControlAction
  | UpdateUserCurveAction
  | DeleteUserCurveAction
  | UpdateControlHeightAction
  | RotateControlAction
  | UpdateQuickPropertiesAction
  | MappedDataAction
  | SaveListGraphNameAction
  | ExitResetPassAction
  | DeleteTcGroupAction
  | SaveListGraphAction

// アクションクリエーター
export const actionCreators = {
  login: (credential: Model.Credential) =>
  ({
    type: "LOGIN",
    payload: {credential}
  } as LoginAction),
  verificationCode: (code: string) =>
  ({
    type: "VERIFICATION_CODE",
    payload: {code}
  } as verificationCodeAction),
  cancleVerificationCode: () =>
  ({
    type: "CANCLE_VERIFICATION_CODE"
  } as cancleVerificationCodeAction),
  changePassword: (currentPassword: string, newPassword: string) =>
  ({
    type: "CHANGE_PASS",
    payload: {
      currentPassword, newPassword
    }
  } as ChangePassAction),
  exitResetPassword: () =>
  ({
    type: "EXIT_RESET_PASS",
  } as ExitResetPassAction),
  init: () =>
  ({
    type: "INIT_APP",
  } as InitAppAction),
  clearSession: () =>
  ({
    type: "CLEAR_SESSION",
  } as ClearSessionAction),
  showMessage: (message: Message, dissmissAfterMs: number = 5000) =>
  ({
    type: "SHOW_MESSAGE",
    payload: {
      message,
      dissmissAfterMs,
    },
  } as ShowMessageAction),
  dismissMessage: (messageId: string) =>
  ({
    type: "DISMISS_MESSAGE",
    payload: {
      messageId,
    },
  } as DismissMessageAction),
  fetch: (
    fetchId: string,
    url: string,
    method: HttpMethod,
    params: object | null,
    multipart: boolean = false,
    json: boolean = false,
    retry: number = 0,
  ) =>
  ({
    type: "FETCH",
    payload: {
      fetchId,
      url,
      method,
      params,
      multipart,
      json,
      retry,
    },
  } as FetchAction),

  initFetch: (fetchId: string) =>
  ({
    type: "INIT_FETCH",
    payload: {
      fetchId,
    },
  } as InitFetchAction),
  discardFetch: (fetchId: string) =>
  ({
    type: "DISCARD_FETCH",
    payload: {
      fetchId,
    },
  } as DiscardFetchAction),
  initForm: (formId: string, initialValue: any) =>
  ({
    type: "INIT_FORM",
    payload: {
      formId,
      initialValue: initialValue,
    },
  } as InitFormAction),
  touchForm: (formId: string, key: string) =>
  ({
    type: "TOUCH_FORM",
    payload: {
      formId,
      key,
    },
  } as TouchFormAction),
  changeForm: (formId: string, name: string, newValue: any) =>
  ({
    type: "CHANGE_FORM",
    payload: {
      formId,
      name,
      newValue,
    },
  } as ChangeFormAction),
  validateForm: (formId: string, errors: FormState["errors"] = {}) =>
  ({
    type: "VALIDATE_FORM",
    payload: {
      formId,
      errors,
    },
  } as ValidateFormAction),
  submitForm: (formId: string) =>
  ({
    type: "SUBMIT_FORM",
    payload: {
      formId,
    },
  } as SubmitFormAction),
  submissionComplete: (formId: string) =>
  ({
    type: "FORM_SUBMISSION_COMPLETED",
    payload: {
      formId,
    },
  } as FormSubmissionCompletedAction),
  resetForm: (formId: string) =>
  ({
    type: "RESET_FORM",
    payload: {
      formId,
    },
  } as ResetFormAction),
  discardForm: (formId: string) =>
  ({
    type: "DISCARD_FORM",
    payload: {
      formId,
    },
  } as DiscardFormAction),
  downloadFile: (
    fetchId: string,
    title: string,
    expectedFilename: string,
    expectedMimeType: string,
    url: string,
    params?: any,
    method?: string,
    searchParams: boolean = false
  ) =>
  ({
    type: "DOWNLOAD_FILE",
    payload: {
      fetchId,
      title,
      expectedFilename,
      expectedMimeType,
      url,
      params,
      method,
      searchParams,
    },
  } as DownloadFileAction),
  setContainer: (producerCode: number, date: string) =>
  ({
    type: "SET_CONTAINER",
    payload: {
      producerCode,
      date,
    },
  } as SetContainerAction),
  setSearchParams: (params: string) =>
  ({
    type: "SET_SEARCH_PARAMS",
    payload: {
      params,
    },
  } as SetSearchParamsAction),
  setPageSize: (pageSize: number) =>
  ({
    type: "SET_PAGE_SIZE",
    payload: {
      pageSize,
    },
  } as SetPageSizeAction),
};

export const selectors = {
  isAuthorize: (state: ApplicationState) => {
    //FIX LOG IN
    //HANDLE USER
    // if (state.app.user) {
    //   const cognit_user: any = state.app.user.cognit_user
    //   if (cognit_user.signInUserSession) return true;
    // }
    // return false;
    if (state.app.user) {
      return state.app.user;
    }
    return null;
  },
  //FIX LOG IN
  // isNewPasswordRequired: (state: ApplicationState) => {
  //   if (state.app.user) {
  //     const cognit_user: any = state.app.user.cognit_user
  //     if (cognit_user.signInUserSession) return false;
  //     if(cognit_user.challengeName === 'NEW_PASSWORD_REQUIRED') return true;
  //   } else{
  //     return false;
  //   }
  // },
  // isMFARequired: (state: ApplicationState) => {
  //   if (state.app.user) {
  //     const cognit_user: any = state.app.user.cognit_user
  //     if (cognit_user.signInUserSession) return false;
  //     if(cognit_user.challengeName === 'CUSTOM_CHALLENGE') return true;
  //   } else{
  //     return false;
  //   }
  // },
  // isPasswordResetRequired: (state: ApplicationState) => {
  //   if (state.app.user) {
  //     const cognit_user: any = state.app.user.cognit_user
  //     if (cognit_user.signInUserSession) return false;
  //   } else{
  //     return state.app.isPasswordResetRequired;
  //   }
  // },
  getFormState: (state: ApplicationState, formId: string) => {
    if (!(state.app.forms && state.app.forms[formId])) {
      return {
        value: undefined,
        valid: false,
        errors: {},
        touched: {},
        error: false,
        submitting: false,
        validating: false,
      };
    }
    const {
      value,
      submitting,
      error,
      pristine,
      errors,
      touched,
      validationNeeded,
    } = state.app.forms[formId];
    let retErrors: { [fieldName: string]: string | undefined } = {};
    if (!pristine) {
      retErrors = errors;
    } else if (errors && touched) {
      for (let prop in touched) {
        retErrors[prop] = errors[prop];
      }
    }
    return {
      value: value,
      valid: !validationNeeded && !error,
      errors: retErrors,
      touched: touched,
      error: error,
      submitting: submitting,
      validating: validationNeeded,
    };
  },
  getFetchState: (state: ApplicationState, fetchId: string): any => {
    if (!(state.app.fetch && state.app.fetch[fetchId])) {
      return {
        loading: false,
      };
    }
    return state.app.fetch[fetchId];
  },
};

export const middleware: Middleware<{}, ApplicationState> = ({
  dispatch,
  getState,
}) => (next) => (incomingAction: Action) => {
  const action = incomingAction as KnownAction;
  if (
    ![
      "UPDATE_PROPERTIES_OF_CONTROL",
      "REMOVE_SHAPES",
      "CUT_SHAPES"
    ]
    .includes(action.type)
  ) next(action);
  if (action.type === "INIT_APP") {
    dispatch({
      type: "CLEAR_SESSION",
    } as ClearSessionAction);
    dispatch({
      type: "INIT_APP_COMPLETED",
    } as any);
    dispatch({
      type: "APP_LOADED",
    } as AppLoadedAction);
    //FIX LOG IN
    // セッション情報の取得
    // sessionStorage.setItem("prevLocation", window.location.pathname);
    // Auth.currentAuthenticatedUser()
    //   .then((cognitoUser) => {
    //     dispatch({
    //       type: "SESSION_FETCHED",
    //       payload: {cognitoUser:cognitoUser},
    //     } as SessionFetchedAction);
    //   })
    //   .catch(() => {
    //     dispatch({
    //       type: "CLEAR_SESSION",
    //     } as ClearSessionAction);
    //     dispatch({
    //       type: "INIT_APP_COMPLETED",
    //     } as any);
    //   }).finally(() => {
    //     dispatch({
    //       type: "APP_LOADED",
    //     } as AppLoadedAction);
    //   });
  }
  if (action.type === "CLEAR_SESSION") {
    // Auth.signOut().finally(() => {
    //   window.sessionStorage.clear();
    // });
  }
  if (action.type === "SHOW_MESSAGE") {
    const appState = getState();
    const message = appState.app.messages.find(
      (m) => m.id === action.payload.message.id
    );
    if (!message) {
      const messageId = action.payload.message.id || generateRandomUUID();
      dispatch({
        type: "MESSAGE_ADDED",
        payload: {
          id: messageId,
          ...action.payload.message,
        },
      } as MessageAddedAction);
      if (action.payload.dissmissAfterMs > 0) {
        setTimeout(() => {
          dispatch({
            type: "DISMISS_MESSAGE",
            payload: {
              messageId: messageId,
            },
          } as DismissMessageAction);
        }, action.payload.dissmissAfterMs);
      }
    }
  }
  if (action.type === "FETCH") {
    const { fetchId, url, method, params, multipart, json } = action.payload;
    const fullUrl = process.env.REACT_APP_APIGW_URL + url;
    const appState = getState();
    const dataState = appState.app.fetch[fetchId];
    // const token = "token";
    let headers = new Headers();
    // headers.append("Authorization", token);

    if (!dataState || dataState.loading) {
      let body: any = undefined;
      if (params) {
        if (json) {
          body = JSON.stringify(params);
        } else {
          if (multipart) {
            body = new FormData();
          } else {
            body = new URLSearchParams();
          }
          if (method === "GET") {
            params.forEach((value: any, key: string) => {
              body.set(key, value);
            });
          }
          else {
            for (let prop in params) {
              if (typeof params[prop] === 'object' && params[prop] !== null) {
                for (let propChill in params[prop]) {
                  body.set(prop + "." + propChill, params[prop][propChill]);
                }
              } else {
                body.set(prop, params[prop]);
              }
            }
          }
        }
      }
      let fetchTask;
      if (method === "GET" || method === "DELETE") {
        let requestUrl;
        if (body) {
          requestUrl =
            fullUrl.indexOf("?") > -1
              ? fullUrl + "&" + body.toString()
              : fullUrl + "?" + body.toString();
        } else {
          requestUrl = fullUrl;
        }
        fetchTask = fetch(requestUrl, { headers: headers, credentials: 'omit', method: method });
      } else {
        if (json) {
          headers.append("Content-Type", "application/json");
          fetchTask = fetch(fullUrl, {
            headers: headers,
            credentials: 'omit',
            method: method,
            body: body,
          });
        } else {
          fetchTask = fetch(fullUrl, {
            headers: headers,
            credentials: 'omit',
            method: method,
            body: body,
          });
        }
      }
      fetchTask
        .then((res) => {
          if (res.status === 401 || res.status === 403) {
            dispatch({
              type: "SESSION_EXPIRED",
            } as SessionExpiredAction);
          }
          const contentType = res.headers.get("content-type");
          if (contentType && contentType.indexOf("application/json") !== -1) {
            res
              .json()
              .then((data) => {
                dispatch({
                  type: "FETCH_COMPLETED",
                  payload: {
                    fetchId: fetchId,
                    success: res.ok || res.status === 204,
                    data: data,
                  },
                } as FetchCompletedAction);
              })
              .catch((err) => {
                dispatch({
                  type: "FETCH_COMPLETED",
                  payload: {
                    fetchId: fetchId,
                    success: false,
                    data: null,
                    error: err,
                  },
                } as FetchCompletedAction);
              });
          } else {
            res
              .blob()
              .then((data) => {
                dispatch({
                  type: "FETCH_COMPLETED",
                  payload: {
                    fetchId: fetchId,
                    success: res.ok || res.status === 204,
                    data: data,
                  },
                } as FetchCompletedAction);
              })
              .catch((err) => {
                dispatch({
                  type: "FETCH_COMPLETED",
                  payload: {
                    fetchId: fetchId,
                    success: false,
                    data: null,
                    error: err,
                  },
                } as FetchCompletedAction);
              });
          }
        })
        .catch((err) => {
          dispatch({
            type: "FETCH_COMPLETED",
            payload: {
              fetchId: fetchId,
              success: false,
              data: null,
              error: err,
            },
          } as FetchCompletedAction);
        });
    }
    // Auth.currentSession().then((session) => {
    //   const { fetchId, url, method, params, multipart, json } = action.payload;
    //   const fullUrl = process.env.REACT_APP_APIGW_URL + url;
    //   const appState = getState();
    //   const dataState = appState.app.fetch[fetchId];
    //   const token = session.getIdToken().getJwtToken();
    //   let headers = new Headers();
    //   headers.append("Authorization", token);

    //   if (!dataState || dataState.loading) {
    //     let body: any = undefined;
    //     if (params) {
    //       if (json) {
    //         body = JSON.stringify(params);
    //       } else {
    //         if (multipart) {
    //           body = new FormData();
    //         } else {
    //           body = new URLSearchParams();
    //         }
    //         if (method === "GET") {
    //           params.forEach((value: any, key: string) => {
    //             body.set(key, value);
    //           });
    //         }
    //         else {
    //           for (let prop in params) {
    //             if (typeof params[prop] === 'object' && params[prop] !== null) {
    //               for (let propChill in params[prop]) {
    //                 body.set(prop + "." + propChill, params[prop][propChill]);
    //               }
    //             } else {
    //               body.set(prop, params[prop]);
    //             }
    //           }
    //         }
    //       }
    //     }
    //     let fetchTask;
    //     if (method === "GET" || method === "DELETE") {
    //       let requestUrl;
    //       if (body) {
    //         requestUrl =
    //           fullUrl.indexOf("?") > -1
    //             ? fullUrl + "&" + body.toString()
    //             : fullUrl + "?" + body.toString();
    //       } else {
    //         requestUrl = fullUrl;
    //       }
    //       fetchTask = fetch(requestUrl, { headers: headers, credentials: 'omit', method: method });
    //     } else {
    //       if (json) {
    //         headers.append("Content-Type", "application/json");
    //         fetchTask = fetch(fullUrl, {
    //           headers: headers,
    //           credentials: 'omit',
    //           method: method,
    //           body: body,
    //         });
    //       } else {
    //         fetchTask = fetch(fullUrl, {
    //           headers: headers,
    //           credentials: 'omit',
    //           method: method,
    //           body: body,
    //         });
    //       }
    //     }
    //     fetchTask
    //       .then((res) => {
    //         if (res.status === 401 || res.status === 403) {
    //           dispatch({
    //             type: "SESSION_EXPIRED",
    //           } as SessionExpiredAction);
    //         }
    //         const contentType = res.headers.get("content-type");
    //         if (contentType && contentType.indexOf("application/json") !== -1) {
    //           res
    //             .json()
    //             .then((data) => {
    //               dispatch({
    //                 type: "FETCH_COMPLETED",
    //                 payload: {
    //                   fetchId: fetchId,
    //                   success: res.ok || res.status === 204,
    //                   data: data,
    //                 },
    //               } as FetchCompletedAction);
    //             })
    //             .catch((err) => {
    //               dispatch({
    //                 type: "FETCH_COMPLETED",
    //                 payload: {
    //                   fetchId: fetchId,
    //                   success: false,
    //                   data: null,
    //                   error: err,
    //                 },
    //               } as FetchCompletedAction);
    //             });
    //         } else {
    //           res
    //             .blob()
    //             .then((data) => {
    //               dispatch({
    //                 type: "FETCH_COMPLETED",
    //                 payload: {
    //                   fetchId: fetchId,
    //                   success: res.ok || res.status === 204,
    //                   data: data,
    //                 },
    //               } as FetchCompletedAction);
    //             })
    //             .catch((err) => {
    //               dispatch({
    //                 type: "FETCH_COMPLETED",
    //                 payload: {
    //                   fetchId: fetchId,
    //                   success: false,
    //                   data: null,
    //                   error: err,
    //                 },
    //               } as FetchCompletedAction);
    //             });
    //         }
    //       })
    //       .catch((err) => {
    //         dispatch({
    //           type: "FETCH_COMPLETED",
    //           payload: {
    //             fetchId: fetchId,
    //             success: false,
    //             data: null,
    //             error: err,
    //           },
    //         } as FetchCompletedAction);
    //       });
    //   }
    // }).catch((err) => {
    //   dispatch({
    //     type: "SESSION_EXPIRED",
    //   } as SessionExpiredAction);
    // });
  }
  if (action.type === "DOWNLOAD_FILE") {
    const {
      fetchId,
      title,
      expectedFilename,
      expectedMimeType,
      url,
      params,
      method,
      searchParams,
    } = action.payload;
    let fetchTask;
    let body = undefined;
    if (method === "POST") {
      if (searchParams) {
        body = new URLSearchParams();
        if (params) {
          for (let prop in params) {
            body.set(prop, params[prop]);
          }
        }
        fetchTask = fetch(url, {
          method: method,
          body: body,
        });
      }
      else {
        if (params) {
          body = JSON.stringify(params);
        }
        var headers = new Headers();
        headers.append("Content-Type", "application/json");
        fetchTask = fetch(url, {
          headers: headers,
          method: method,
          body: body,
        });
      }
    }
    else {
      if (params) {
        body = new URLSearchParams();
        for (let prop in params) {
          body.set(prop, params[prop]);
        }
      }
      let requestUrl;
      if (body) {
        requestUrl =
          url.indexOf("?") > -1
            ? url + "&" + body.toString()
            : url + "?" + body.toString();
      } else {
        requestUrl = url;
      }
      fetchTask = fetch(requestUrl);
    }
    let filename: string;
    fetchTask
      .then((res) => {
        if (res.status === 403) {
          dispatch({
            type: "SESSION_EXPIRED",
          } as SessionExpiredAction);
        }
        if (res.ok) {
          filename = expectedFilename;
          return res
            .blob()
            .then((data) => {
              if (data.type !== expectedMimeType) {
                dispatch(
                  actionCreators.showMessage({
                    type: "error",
                    title: title,
                    body: "ダウンロードに失敗しました。",
                  })
                );
                return;
              }
              let link = document.createElement("a");
              link.download = filename;
              link.href = URL.createObjectURL(data);
              link.click();
              URL.revokeObjectURL(link.href);
            })
            .catch((err) => {
              dispatch(
                actionCreators.showMessage({
                  type: "error",
                  title: title,
                  body: "ダウンロードに失敗しました。",
                  detail: err,
                })
              );
            });
        } else {
          dispatch(
            actionCreators.showMessage({
              type: "error",
              title: title,
              body: "ダウンロードに失敗しました。",
            })
          );
        }
      })
      .catch((err) => {
        dispatch(
          actionCreators.showMessage({
            type: "error",
            title: title,
            body: "ダウンロードに失敗しました。",
            detail: err,
          })
        );
      })
      .finally(() => {
        dispatch({
          type: "FETCH_COMPLETED",
          payload: {
            fetchId: fetchId,
            success: true,
          },
        } as FetchCompletedAction);
      });
  }
  if (action.type === "DISMISS_MESSAGE") {
    const appState = getState();
    const message = appState.app.messages.find(
      (m) => m.id === action.payload.messageId
    );
    if (message) {
      dispatch({
        type: "MESSAGE_REMOVED",
        payload: message,
      } as MessageRemovedAction);
    }
  }
  //FIX LOG IN
  // if (action.type === "SESSION_EXPIRED") {
  //   dispatch(
  //     actionCreators.showMessage({
  //       type: "error",
  //       title: "セッションタイムアウト",
  //       body: "セッションの継続時間が切れました。再度ログインしてください。",
  //     })
  //   );
  //   dispatch(actionCreators.init());
  // }
};

const initialData : AppStore = {
  course: [],
}

export const reducer: Reducer<AppState> = (state, incomingAction: Action) => {
  const action = incomingAction as any;
  if (state === undefined) {
    return {
        loading: true,
        initAppLoading: true,
        messages: [],
        user: null,
        resetPassUserName: "",
        isPasswordResetRequired: false,
        fetch: {},
        forms: {},
        container: {
            producerCode: 0,
            date: "",
        },
        searchParams: "",
        pageSize: 100,
        data: JSON.parse(JSON.stringify(initialData)) as AppStore,
    };
  }
  switch (action.type) {
    case "INIT_APP":
      return {
        ...state,
        loading: true,
        initAppLoading: true,
      };
    case "INIT_APP_COMPLETED":
      return {
        ...state,
        initAppLoading: false,
      };
    case "APP_LOADED":
      return {
        ...state,
        loading: false,
        user:null
      };
    case "APP_LOADING":
      return {
        ...state,
        loading:true,
      }
    case "CLEAR_SESSION":
      return {
        ...state,
        user: null,
      };
    case "SESSION_FETCHED":
      return {
        ...state,
        user: action.payload.userProfile,
        resetPassUserName: "",
        isPasswordResetRequired: false,
      };
    case "SESSION_EXPIRED":
      return {
        ...state,
        user: null,
      };
    case "AUTHORIZE":
      return {
        ...state,
        user: action.payload.userProfile,
      };
    case "CANCLE_VERIFICATION_CODE":
      return {
        ...state,
        user: null,
      };
    case "MESSAGE_ADDED":
      return {
        ...state,
        messages: [...state.messages, action.payload],
      };
    case "MESSAGE_REMOVED":
      return {
        ...state,
        messages: state.messages.filter((m) => m !== action.payload),
      };
    case "RESET_PASS":
      const { resetPassUserName } = action.payload;
        return {
          ...state,
          resetPassUserName: resetPassUserName,
          isPasswordResetRequired: true,
        };
    case "EXIT_RESET_PASS":
            return {
              ...state,
              resetPassUserName: "",
              isPasswordResetRequired: false,
            };
    case "INIT_FORM": {
      const { formId, initialValue } = action.payload;
      return {
        ...state,
        forms: {
          ...state.forms,
          [formId]: {
            pristine: true,
            initialValue: initialValue,
            value: initialValue,
            validationNeeded: true,
            errors: {},
            touched: {},
            error: false,
            submitting: false,
          },
        },
      };
    }
    case "DISCARD_FORM": {
      const newState = { ...state };
      delete newState.forms[action.payload.formId];
      return newState;
    }
    case "TOUCH_FORM":
      {
        const { formId, key } = action.payload;
        const currentForm = state.forms[formId];
        if (currentForm) {
          const { touched, pristine } = currentForm;
          if (!pristine) {
            return state;
          }
          return {
            ...state,
            forms: {
              ...state.forms,
              [formId]: {
                ...currentForm,
                touched: { ...touched, [key]: true },
              },
            },
          };
        }
      }
      break;
    case "CHANGE_FORM":
      {
        const { formId, name, newValue } = action.payload;
        const currentForm = state.forms[formId];
        if (currentForm) {
          const touched = { ...currentForm.touched };
          currentForm.touched[name] = true;
          return {
            ...state,
            forms: {
              ...state.forms,
              [formId]: {
                ...currentForm,
                value: { ...currentForm.value, [name]: newValue },
                touched: touched,
                validationNeeded: true,
              },
            },
          };
        }
      }
      break;
    case "VALIDATE_FORM":
      {
        const { formId, errors } = action.payload;
        const currentForm = state.forms[formId];
        if (currentForm) {
          let hasError = false;
          for (let prop in errors) {
            if (errors[prop]) {
              hasError = true;
              break;
            }
          }
          return {
            ...state,
            forms: {
              ...state.forms,
              [formId]: {
                ...currentForm,
                errors,
                error: hasError,
                validationNeeded: false,
              },
            },
          };
        }
      }
      break;
    case "SUBMIT_FORM":
      {
        const { formId } = action.payload;
        const currentForm = state.forms[formId];
        if (currentForm) {
          const { pristine, error } = currentForm;
          if (pristine || !error) {
            return {
              ...state,
              forms: {
                ...state.forms,
                [formId]: {
                  ...currentForm,
                  pristine: false,
                  submitting: !error,
                },
              },
            };
          }
        }
      }
      break;
    case "FORM_SUBMITTED":
      {
        const { formId } = action.payload;
        const currentForm = state.forms[formId];
        if (currentForm && !currentForm.error) {
          return {
            ...state,
            forms: {
              ...state.forms,
              [formId]: {
                ...currentForm,
                submitting: true,
              },
            },
          };
        }
      }
      break;
    case "FORM_SUBMISSION_COMPLETED":
      {
        const { formId } = action.payload;
        const currentForm = state.forms[formId];
        if (currentForm) {
          return {
            ...state,
            forms: {
              ...state.forms,
              [formId]: {
                ...currentForm,
                submitting: false,
              },
            },
          };
        }
      }
      break;
    case "RESET_FORM":
      {
        const { formId } = action.payload;
        const currentForm = state.forms[formId];
        if (currentForm) {
          return {
            ...state,
            forms: {
              ...state.forms,
              [formId]: {
                pristine: true,
                initialValue: currentForm.initialValue,
                value: currentForm.initialValue,
                validationNeeded: true,
                errors: {},
                touched: {},
                error: false,
                submitting: false,
              },
            },
          };
        }
      }
      break;
    case "INIT_FETCH":
      {
        const { fetchId } = action.payload;
        const currentData = state.fetch[fetchId];
        if (!currentData || !currentData.loading) {
          return {
            ...state,
            fetch: {
              ...state.fetch,
              [fetchId]: {
                requested: false,
                loading: false,
              },
            },
          };
        }
      }
      break;
    case "DISCARD_FETCH": {
      const { fetchId } = action.payload;
      const newState = { ...state };
      delete newState.fetch[fetchId];
      return newState;
    }
    case "FETCH":
    case "DOWNLOAD_FILE":
      {
        const { fetchId } = action.payload;
        const currentData = state.fetch[fetchId];
        if (currentData) {
          return {
            ...state,
            fetch: {
              ...state.fetch,
              [fetchId]: {
                ...currentData,
                requested: true,
                loading: true,
              },
            },
          };
        }
      }
      break;
    case "FETCH_COMPLETED":
      {
        const { fetchId, success, data, error } = action.payload;
        const currentData = state.fetch[fetchId];
        if (currentData) {
          return {
            ...state,
            fetch: {
              ...state.fetch,
              [fetchId]: {
                ...currentData,
                loading: false,
                success,
                data,
                error,
              },
            },
          };
        }
      }
      break;
    case "SET_CONTAINER": {
      const { producerCode, date } = action.payload;
      const currentData = state.container;
      return {
        ...state,
        container: {
          ...currentData,
          producerCode: producerCode,
          date: date,
        },
      };
    }
    case "SET_SEARCH_PARAMS": {
      const { params } = action.payload;
      return {
        ...state,
        searchParams: params,
      };
    }
    case "SET_PAGE_SIZE": {
      const { pageSize } = action.payload;
      return {
        ...state,
        pageSize: pageSize,
      };
    }
  }
  return state;
};